import { Injectable } from '@angular/core';
import { Body, Delete, Get, HttpService, Patch, Path, Post } from '@rfx/ngx-http';
import { from, Observable, switchMap, concatMap, of } from 'rxjs';
import { Gait, RideCategory } from '../enums';
import { Ride, RideGaitMetrics, RidePath } from '../models';
import { flatMap, GaitNumbers, getBoundingRectangle, WayPoint } from '@caballus/common';
import * as jsonToFormData from 'json-form-data';
import { tap } from 'rxjs/operators';
import { chunk } from 'lodash';

interface StartRideDto {
    _id: string;
    startDateTime: Date;
    horseIds: string[];
}

interface EndRideDto {
    _id: string;
    endDateTime: Date;
    startDateTime: Date;
    pauseTimeInSeconds: number;
    horseIds: string[];
    paths: PathDto[];
    distanceKilometers: number;
}

interface PathDto {
    startDateTime: Date;
    endDateTime: Date;
    wayPoints: WayPointDto[];
}

interface WayPointDto {
    timestamp: Date;
    longitude: number;
    latitude: number;
}

interface GaitMetricDto {
    gait: Gait;
    metric: number;
}

interface RideGaitMetricsDto {
    horseId: string;
    metrics: GaitMetricDto[];
}

interface RideDetailsDto {
    distanceKilometers: number;
    category: RideCategory;
    notes?: string;
    paths?: PathDto[];
    calculatedGaitMinutes: RideGaitMetricsDto[];
    manualGaitMinutes: RideGaitMetricsDto[];
    calculatedGaitKilometers: RideGaitMetricsDto[];
    _id: string;
    endDateTime?: Date;
    startDateTime?: Date;
    pauseTimeInSeconds?: number;
    horseIds?: string[];
    location?: string;
    pausedWaypoints?: WayPointDto[];
}

interface RideEntryDto {
    _id: string;
    horseIds?: string[];
    distanceKilometers: number;
    category: RideCategory;
    notes?: string;
    endDateTime?: Date;
    startDateTime?: Date;
    name?: string;
}

// Add new interface for chunked data
interface ChunkedRideData {
    chunkIndex: number;
    totalChunks: number;
    rideId: string;
    chunk: Partial<EndRideDto>;
}

@Injectable({
    providedIn: 'root'
})
export class RideService extends HttpService {
    //in theory a waypoint takes 191 bytes with all its data
    //we have a hard cap of 100kb per request so 100000/191 = 523 waypoints per chunk
    //but to be safe we'll do 300
    private readonly CHUNK_SIZE = 300; // 10KB chunks

    @Get('/ride/:id')
    public getRideById(@Path('id') id: string): Observable<Ride> {
        return null;
    }

    @Get('/ride/final')
    public finalRide(): Observable<any> {
        return null;
    }

    @Post('/ride/start')
    public startRide(@Body() dto: StartRideDto): Observable<string> {
        return null;
    }

    @Post('/ride/createEmptyRide')
    public createEmptyRide(): Observable<Ride> {
        return null;
    }

    public updateEntryRide(@Body() dto: RideEntryDto): Observable<Ride> {
        const formData = jsonToFormData(dto);
        return from(this._generateRidePicture(new Ride())).pipe(
            tap(pictureFile => formData.append('ridePicture', pictureFile)),
            switchMap(() => this._http.post<Ride>('/ride/updateEntryRide', formData))
        );
    }

    public endRide(dto: EndRideDto): Observable<void> {
        // If there are no paths, send directly
        if (!dto.paths?.length) {
            return this._http.patch<void>('/ride/end', dto);
        }

        // Get the first path's waypoints
        const firstPath = dto.paths[0];
        const waypoints = firstPath.wayPoints;

        // Split waypoints into chunks
        const waypointChunks = chunk(waypoints, this.CHUNK_SIZE); // Adjust chunk size as needed

        // Create chunked data array
        const chunks: ChunkedRideData[] = waypointChunks.map((waypointChunk, index) => {
            // Create a modified path with chunked waypoints
            const modifiedPath = {
                ...firstPath,
                wayPoints: waypointChunk
            };

            // Create the rest of the DTO without paths
            const dtoWithoutPaths = { ...dto };
            delete dtoWithoutPaths.paths;

            return {
                chunkIndex: index,
                totalChunks: waypointChunks.length,
                rideId: dto._id,
                chunk: {
                    ...dtoWithoutPaths,
                    paths: [modifiedPath]
                }
            };
        });

        // Send chunks sequentially
        return from(chunks).pipe(
            concatMap(chunk => this._http.post<void>('/ride/end/chunk', chunk)),
            concatMap((_, index) => {
                // After last chunk, finalize the upload
                if (index === chunks.length - 1) {
                    return this._http.post<void>('/ride/end/finalize', { rideId: dto._id });
                }
                return of(null);
            })
        );
    }

    @Delete('/ride/:id')
    public deleteRide(@Path('id') id: string): Observable<void> {
        return null;
    }

    @Delete('/ride/deleteEntyRide/:rideId')
    public deleteEntyRide(@Path('rideId') rideId: string): Observable<void> {
        return null;
    }

    public saveRideDetails(ride: Ride): Observable<void> {
        const formData = jsonToFormData(this._rideToRideDetailsDto(ride));
        return from(this._generateRidePicture(ride)).pipe(
            tap(pictureFile => formData.append('ridePicture', pictureFile)),
            switchMap(() => this._http.patch<void>('/ride/details', formData))
        );
    }

    private _rideToRideDetailsDto(r: Ride): RideDetailsDto {
        const gaitNumbersToDto = (gn: GaitNumbers): GaitMetricDto[] => {
            const arr = [];
            for (const m of Gait.members) {
                const key = Gait[m];
                arr.push({ gait: m, metric: gn[m] });
            }
            return arr;
        };
        const rideGaitMetricsToDto = (m: RideGaitMetrics): RideGaitMetricsDto => ({
            horseId: m.horseId,
            metrics: gaitNumbersToDto(m.metrics)
        });
        return {
            distanceKilometers: r.distanceKilometers,
            category: r.category,
            notes: r.notes ? r.notes : null,
            calculatedGaitMinutes: r.calculatedGaitMinutes.map(cgm => rideGaitMetricsToDto(cgm)),
            manualGaitMinutes: r.manualGaitMinutes.map(mgm => rideGaitMetricsToDto(mgm)),
            calculatedGaitKilometers: r.calculatedGaitKilometers.map(cgk =>
                rideGaitMetricsToDto(cgk)
            ),
            _id: r._id,
            endDateTime: r.endDateTime ? new Date(r.endDateTime) : null,
            startDateTime: r.startDateTime ? new Date(r.startDateTime) : null,
            pauseTimeInSeconds: isNaN(r.pauseTimeInSeconds) ? 0 : r.pauseTimeInSeconds,
            horseIds: r.horseIdentities.map(identity => identity._id),
            pausedWaypoints: r.pausedWaypoints
        };
    }

    public _generateRidePicture(ride: Ride): Promise<File> {
        return new Promise((resolve, reject) => {
            // Create a hidden canvas element
            const canvas = document.createElement('canvas');
            canvas.style.display = 'none';

            // Set the canvas size
            canvas.width = 200;
            canvas.height = 200;

            // Get the canvas context
            const ctx = canvas.getContext('2d');

            // Draw a circle on the canvas
            const geoPoints = flatMap(ride.paths, path => path.wayPoints);
            const boundingRect = getBoundingRectangle(geoPoints, false);
            const scale = Math.min(canvas.width, canvas.height);
            // Draw background
            ctx.fillStyle = '#d3eccb';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            // Draw path
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            //seperate paths based on pauses
            let currentPath: WayPoint[] = [];
            let paths: WayPoint[][] = [];
            const waypointTimestamps = ride.pausedWaypoints.map(
                (wp: WayPoint) => new Date(wp.timestamp)
            );
            ride.paths.forEach((ridePath: RidePath) => {
                ridePath.wayPoints.forEach((wp: WayPoint) => {
                    const timeStamp = new Date(wp.timestamp);

                    if (
                        waypointTimestamps.some(
                            (pausedWaypoint: Date) =>
                                pausedWaypoint === timeStamp ||
                                `${pausedWaypoint}` === `${timeStamp}`
                        )
                    ) {
                        currentPath.push(wp);
                        paths.push(currentPath);
                        currentPath = [];
                    } else {
                        currentPath.push(wp);
                    }
                });
            });
            ctx.translate(10, 10);
            paths.push(currentPath);
            ctx.beginPath();
            for (let path of paths) {
                if (path.length > 0) {
                    ctx.beginPath();
                    const { latitude, longitude } = path[0];
                    const rightOffset = boundingRect.width * 0.1;
                    const topOffset = boundingRect.height * 0.1;
                    const x =
                        ((latitude - boundingRect.x) / (boundingRect.width * 1.1) + rightOffset) *
                        scale;
                    const y =
                        ((longitude - boundingRect.y) / (boundingRect.height * 1.1) + topOffset) *
                        scale;
                    ctx.moveTo(x, y);

                    for (let i = 1; i < path.length; i++) {
                        const { latitude, longitude } = path[i];
                        const x =
                            ((latitude - boundingRect.x) / (boundingRect.width * 1.1) +
                                rightOffset) *
                            scale;
                        const y =
                            ((longitude - boundingRect.y) / (boundingRect.height * 1.1) +
                                topOffset) *
                            scale;
                        ctx.lineTo(x, y);
                    }
                    ctx.stroke();
                    ctx.closePath();
                }
            }

            canvas.toBlob(
                blob =>
                    resolve(
                        new File([blob], `${new Date().getTime()}_ride.png`, { type: 'image/png' })
                    ),
                'image/png'
            );
        });
    }

    @Post('/ride/saveOngoingRide')
    public saveOngoingRide(@Body('id') id: string): Observable<void> {
        return null;
    }

    //returns the number of waypoints that were synced, since the queue is changing constantly
    @Post('/ride/:id/addWaypointsToSync')
    public addWaypointsToSync(
        @Body() waypoints: { wayPoints: WayPoint[] },
        @Path('id') rideId: string
    ): Observable<number> {
        return null;
    }

    public getClosestLocationName(lat: number, lon: number): Observable<string> {
        const map = new google.maps.Map(document.createElement('div'));
        const placesService = new google.maps.places.PlacesService(map);
        const searchRequest = {
            location: new google.maps.LatLng(lon, lat),
            radius: 1609.34, //1 mile in meters
            types: '(place)'
        };
        return new Observable<string>(observer => {
            placesService.nearbySearch(searchRequest, (results, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    const nearbyLocations = results;

                    // Process the results and extract the name of the closest location
                    // For example, if you want to return the name of the first result:
                    const closestLocation: google.maps.places.PlaceResult =
                        this._getClosestLocation(nearbyLocations, new google.maps.LatLng(lon, lat));
                    observer.next(closestLocation.name); // Emit the closest location name
                    observer.complete(); // Complete the observable
                } else {
                    observer.next('No nearby location found');
                    observer.complete();
                }
            });
        });
    }

    public _getClosestLocation(
        locations: google.maps.places.PlaceResult[],
        center: google.maps.LatLng
    ): google.maps.places.PlaceResult {
        let closestLocationDistance: number = 0;
        let closestLocationIndex: number = 0;
        const haversineFormula = (
            mk1: google.maps.LatLng,
            mk2: google.maps.LatLng,
            currentLocationIndex: number
        ) => {
            //caluclates the distance between two points
            var R = 3958.8; // Radius of the Earth in miles
            var rlat1 = mk1.lat() * (Math.PI / 180); // Convert degrees to radians
            var rlat2 = mk2.lat() * (Math.PI / 180); // Convert degrees to radians
            var difflat = rlat2 - rlat1; // Radian difference (latitudes)
            var difflon = (mk2.lng() - mk1.lng()) * (Math.PI / 180); // Radian difference (longitudes)

            var d =
                2 *
                R *
                Math.asin(
                    Math.sqrt(
                        Math.sin(difflat / 2) * Math.sin(difflat / 2) +
                            Math.cos(rlat1) *
                                Math.cos(rlat2) *
                                Math.sin(difflon / 2) *
                                Math.sin(difflon / 2)
                    )
                );
            if (currentLocationIndex === 0 || d < closestLocationDistance) {
                closestLocationDistance = d;
                closestLocationIndex = currentLocationIndex;
            }
            return;
        };

        locations.forEach((location, index) => {
            haversineFormula(location.geometry.location, center, index);
        });
        return locations[closestLocationIndex];
    }
}
